import { createAction } from "redux-actions";
import { requestAsync } from "lib/api";
import { getQuery, IQuery } from "queries/companies";
import { activeListClear } from "actions/active-list";
import {
    ANALYTICS_TRACK,
    SCOPE,
    SCOPE_BRAND_CLEAR,
    SCOPE_BRAND_SET,
    SCOPE_BRAND_UPDATE,
    SCOPE_COMPANIES_REQUEST,
    SCOPE_COMPANY,
    SCOPE_COMPANY_CLEAR,
    SCOPE_COMPANY_SET,
    SCOPE_CURRENCY_SET,
    SCOPE_HIDE_MSRP,
    SCOPE_HIDE_WHOLESALE,
    SCOPE_IS_CHILD_FRAME_SET,
    SCOPE_PAGE_CLEAR,
    SCOPE_PAGE_SET,
    SCOPE_SET_USER_TYPE,
    SCOPE_SHOW_TERMS,
    SCOPE_SITE_CLEAR,
    SCOPE_SITE_SET,
    SCOPE_VIEW_AS,
    SCOPE_VIEW_AS_GUEST,
} from "constants/action-types";
import { ITEM_VIEW_TYPE_LARGE_TILE_CLICKED } from "constants/analytics";
import { Dispatch } from "redux";
import { EventTypes } from "redux-segment";
import me from "models/me";
import { IStore } from "reducers";
import { checkStatus } from "actions/base-actions";
import { resetBrandCarriers } from "actions/shipping-accounts";
import { latestTermsSignedEndpoint } from "queries/brand-terms";
import { ICompany } from "components/contacts/contacts-types";
import { fetchWatermarkingDetails } from "actions/watermarking";
import { BrandRecord } from "records/brand";
import { gtm } from "helpers/gtm";

export const activeBrandSet = (payload: any) => {
    return (dispatch, getState: () => IStore) => {
        const store = getState();
        dispatch(activeListClear());
        if (store.scope?.brand?._id && store.scope?.brand?._id !== payload?._id) {
            dispatch(resetBrandCarriers());
        }
        dispatch({
            type: SCOPE_BRAND_SET,
            payload,
        });
        dispatch(fetchWatermarkingDetails());
        const allowGoogleAnalytics =
            (payload?.settings?.preferences?.find((p) => p?.preference === "google_analytics")?.value || false) &&
            payload?.settings?.google_analytics?.is_enabled &&
            payload?.settings?.google_analytics?.measurement_id;
        gtm.config(allowGoogleAnalytics ? payload.settings.google_analytics.measurement_id : null);
    };
};

export const activeSiteSet = (payload: any) => {
    return (dispatch: Dispatch<{}>) => {
        dispatch({
            type: SCOPE_SITE_SET,
            payload,
        });
    };
};

export const activeBrandUpdate = (payload: BrandRecord) => {
    return (dispatch: Dispatch<{}>) => {
        dispatch({
            type: SCOPE_BRAND_UPDATE,
            payload,
        });
    };
};

export const activePageSet = (payload: any) => {
    return (dispatch: Dispatch<{}>) => {
        dispatch({
            type: SCOPE_PAGE_SET,
            payload,
        });
    };
};

export const activeBrandClear = createAction(SCOPE_BRAND_CLEAR);
export const activeCompanyClear = createAction(SCOPE_COMPANY_CLEAR);
export const activeCompanySet = createAction<ICompany>(SCOPE_COMPANY_SET);
export const activePageClear = createAction(SCOPE_PAGE_CLEAR);
export const activeSiteClear = createAction(SCOPE_SITE_CLEAR);
export const setViewAs = createAction<ICompany | null | undefined>(SCOPE_VIEW_AS);
export const toggleViewAsGuest = createAction(SCOPE_VIEW_AS_GUEST);

export const fetchActiveCompanies = (query: IQuery) => (dispatch: Dispatch<{}>) =>
    dispatch(requestAsync(getQuery(query))).then(({ status, body }) => {
        if (checkStatus(dispatch, status, body)) {
            return dispatch({ type: SCOPE_COMPANIES_REQUEST, payload: body });
        }
    });

export const fetchLatestTermsSigned = (brandId: string) => (dispatch: Dispatch<{}>) => {
    return dispatch(requestAsync(latestTermsSignedEndpoint(me._id))).then(({ status, body }) => {
        dispatch({ type: SCOPE_SHOW_TERMS, payload: body });
    });
};

export const activeCurrencySet = createAction(SCOPE_CURRENCY_SET);

export const fetchBrandedLogin = (payload) => ({
    payload,
    type: SCOPE.BRANDED_LOGIN.START,
});

export const displaySpinner = (payload: boolean) => ({
    payload,
    type: SCOPE.BRANDED_LOGIN.SPINNER,
});

export const setUserType = (payload: string) => ({
    payload,
    type: SCOPE_SET_USER_TYPE,
});

export const fetchStartOrderCompanies = (payload) => {
    return {
        payload,
        type: SCOPE.COMPANIES.START,
    };
};

export const fetchCompanyAndSetViewAs = (company: ICompany) => ({
    type: SCOPE_COMPANY.START,
    payload: { url: `/api/v3.0/company/${company._id}?__fields=__all`, meta: company },
});

export const setShowTerms = (payload) => {
    return {
        payload,
        type: SCOPE_SHOW_TERMS,
    };
};

export const setIsChildFrame = (isChildFrame: boolean) => ({
    payload: isChildFrame,
    type: SCOPE_IS_CHILD_FRAME_SET,
});

export const setViewType = (viewType: string, brandId: string) => {
    me.setViewPref("item_grid_view_preference", viewType);
    const largeTileViewClicked = viewType === "largeTile" && ITEM_VIEW_TYPE_LARGE_TILE_CLICKED;

    return {
        meta: largeTileViewClicked && {
            analytics: {
                eventPayload: {
                    options: {
                        groupId: brandId,
                    },
                    properties: {
                        brand: brandId,
                    },
                    event: largeTileViewClicked,
                },
                eventType: EventTypes.track,
            },
        },
        type: ANALYTICS_TRACK,
    };
};

interface IToggleHidePrice {
    dispatch: Dispatch<{}>;
    hide: boolean;
    currentValue: boolean;
    prefName: string;
    actionType: string;
}
const toggleHidePrice = ({ dispatch, currentValue, hide, prefName, actionType }: IToggleHidePrice) => {
    /*
     * If a brand does not have 'can_hide_pricing' permisson,
     * in store should be `hide_product_price_msrp` = false and `hide_product_price_wholesale` = false
     */
    if (!me.hasPermission("can_hide_pricing")) {
        if (currentValue) {
            dispatch({ type: actionType, payload: false });
        }

        return;
    }

    const shouldHide: boolean = hide !== undefined ? hide : !currentValue;

    dispatch({ type: actionType, payload: shouldHide });

    if (me.getViewPref(prefName) !== shouldHide) {
        me.setViewPref(prefName, shouldHide, {
            onError: () => {
                // if something went wrong, set what is in `me` object
                dispatch({ type: actionType, payload: me.getViewPref(prefName) });
            },
        });
    }
};

export const toggleHideMsrp = (hide?: boolean) => (dispatch: Dispatch<{}>, getState: () => IStore) => {
    toggleHidePrice({
        dispatch,
        hide,
        actionType: SCOPE_HIDE_MSRP,
        prefName: "hide_product_price_msrp",
        currentValue: getState().scope.hideMsrp,
    });
};

export const toggleHideWholesale = (hide?: boolean) => (dispatch: Dispatch<{}>, getState: () => IStore) => {
    toggleHidePrice({
        dispatch,
        hide,
        actionType: SCOPE_HIDE_WHOLESALE,
        prefName: "hide_product_price_wholesale",
        currentValue: getState().scope.hideWholesale,
    });
};
